import {Injectable, Injector} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject, Subscription as RxSubscription, firstValueFrom} from 'rxjs';
import {JwtHelperService} from '@auth0/angular-jwt';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, map, take} from 'rxjs/operators';
import {SettingsService} from 'app/original-services/settings.service';
import {StorageService} from 'app/original-services/storage.service';
import {LoggerService} from 'app/original-services/logger.service';
import {FirstPromoterService} from 'app/original-services/chargeBee.service';
import moment from 'moment';
import {RoutingService} from 'app/original-services/routing.service';
import {STATE_NAMES} from 'app/common/state-names';
import { IntercomService } from 'app/services/intercom/intercom.service';
import {User} from '../original-models/user.model';
import {SignUpApiPayload} from 'app/models/auth/sign-up';
import {SignIn} from '../original-models/sign-in.model';
import {Subscription} from '../original-models/subscription';
import { ColorThemeFromServerEnum } from 'app/models/profile/color-theme.model';
import {PopupService} from "../services/popup.service";

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    apiUrl: string;
    private _currentTenantSubject = new BehaviorSubject<User>(null);
    private _currentTenantSubscription: RxSubscription;
    private _updateAccessTokenSubject = new BehaviorSubject<boolean>(false);
    private _updateAccessTokenSubscription: RxSubscription;

    private beforeLoginSubject = new Subject<void>();
    private afterLoginSubject = new Subject<void>();
    private beforeRefreshLoginSubject = new Subject<void>();
    private afterRefreshLoginSubject = new Subject<void>();
    private beforeLogoutSubject = new Subject<void>();
    public afterLogoutSubject = new Subject<void>();
    user = new User();

    afterLogin$ = this.afterLoginSubject.asObservable();
    beforeLogout$ = this.beforeLogoutSubject.asObservable();
    afterLogout$ = this.afterLogoutSubject.asObservable();
    newVersionArrived$ = new BehaviorSubject(false);
    private currentTimeout: any;
    activate = false;
    private refreshInMinutes = 2;
    private shiftInMinutes = 1;

    constructor(
        private http: HttpClient,
        private injector: Injector,
        private settings: SettingsService,
        private storage: StorageService,
        public popupService: PopupService,
        private firstPromoterService: FirstPromoterService,
        private logger: LoggerService,
        private intercom: IntercomService,
        public jwtHelper: JwtHelperService
        // ,
        // private activityTrackerService: ActivityTrackerService
    ) {
        this.apiUrl = this.settings.apiUrl;

        if (this.storage.needCopyStorageAuthData()) {
            this.storage.copyStorageAuthData(true);
            this.checkForTiming();
        } else {
            this.checkForTiming();
        }
        let isIEOrEdge = /msie\s|trident\/|edge\//i.test(
            window.navigator.userAgent
        );
        if (!isIEOrEdge) {
            this.storage.localItemChanged().subscribe((data) => {
                if (data) {
                    if (data.processLogout) {
                        window.location.reload();
                    } else if (data.refreshToken === true) {
                        // === true matches only the cross-tab signal emitted by the
                        // StorageService window.storage listener. setLocalItem fires the
                        // same _localItemSubject with the actual token string for same-tab
                        // writes (e.g. our own processLoginData), which we must ignore here
                        // — otherwise this handler runs the idle check on every login/refresh
                        // in this tab and can double-trigger processLogoutData.
                        // Cross-tab path: re-run the idle check against the new token, with
                        // jitter so this tab keeps deferring to whichever sibling performs
                        // the refresh.
                        if (this.currentTimeout) {
                            clearTimeout(this.currentTimeout);
                        }
                        this.checkForTiming(this.shiftInMinutes + Math.random());
                    } else if (data.clearAll && this.storage.selectedLevel) {
                        this.processLogoutData();
                    }
                }
            });
        }
    }

    processAccessToken(
        rememberMe: boolean,
        mfa_token: any
    ) {
        this.storage.rememberMe = rememberMe;
        this.storage.mfaToken = mfa_token;
        this.logger.debug('Processed Mfa Token');
    }

    /**
     * Stores a session that activation already issued. Remember-me stays off, matching the
     * login form default. The browser must not exchange the new password at /auth/token/.
     */
    establishSession(data: any) {
        this.processLoginData(false, data, false);
    }

    private processLoginData(
        rememberMe: boolean,
        data: any,
        isRefresh: boolean
    ) {
        isRefresh
            ? this.beforeRefreshLoginSubject.next()
            : this.beforeLoginSubject.next();
        const jwtPayload = this.jwtHelper.decodeToken(data.access_token);
        if (!isRefresh) {
            // Fresh login proves the user is active right now. The /auth/token/
            // POST is skipped by AuthenticationInterceptor, so lastActivity would
            // otherwise carry a stale value from when the login page first loaded
            // — long enough to trip the 30-min idle check inside checkForTiming.
            this.storage.lastActivity = new Date().getTime();
        }
        this.storage.rememberMe = rememberMe;
        this.storage.accessToken = data.access_token;
        this.storage.refreshToken = data.refresh_token;
        this.storage.userId = jwtPayload.id;
        this.storage.model = jwtPayload.model.split('.')[1];
        this.storage.userEmail = jwtPayload.email;
        this.storage.name = `${jwtPayload.first_name} ${jwtPayload.last_name}`;
        this.storage.firstName = `${jwtPayload.first_name}`;
        isRefresh
            ? this.afterRefreshLoginSubject.next()
            : this.afterLoginSubject.next();
        this.checkForTiming();
        this.logger.debug('Processed login data');
    }

    private checkForTiming(shift?: number) {
        let accessToken = this.storage.accessToken;
        if (accessToken) {
            let now = moment();
            let rememberMe = this.storage.rememberMe;
            let lastActivity = this.storage.lastActivity;
            let endTimeOfAccessToken =
                this.jwtHelper.getTokenExpirationDate(accessToken);
            let difference = moment(endTimeOfAccessToken).diff(now, 'seconds');
            let activityDifference = moment(now).diff(
                new Date(+lastActivity),
                'seconds'
            );
            let tokenTime = 30 * 60; //30 minutes
            if (this.currentTimeout) {
                clearTimeout(this.currentTimeout);
            }
            // if (rememberMe) {
            //     if (difference <= time) {
            //         this.updateAccessToken();
            //     } else {
            //         this.currentTimeout = setTimeout(() => {
            //             this.updateAccessToken();
            //         }, time * 1000);
            //     }
            // } else {

                if (difference <= 0 || activityDifference >= tokenTime) {
                    console.log('last activity: ' + new Date(+lastActivity).toISOString())
                    this.processLogoutData();
                } else {
                    this.delayedRefreshToken(shift);
                }
            // }
        } else {
            this.processLogoutData();
        }
    }

    delayedRefreshToken(shift?: number) {
        this.currentTimeout = setTimeout(() => {
            console.log(`token timeout after ${((shift ? shift : 0) + this.refreshInMinutes)}: ` + new Date().toISOString())
            this.updateAccessToken();
        }, ((shift ? shift : 0) + this.refreshInMinutes) * 60 * 1000);
    }

    isValidDate(dateString: string): boolean {
        const date = new Date(dateString);
        return !isNaN(date.getTime());
    }
    updateAccessToken(): Observable<boolean> {
        if (this.storage.refreshToken && (!this.storage.requestTokenSent ||
            (this.isValidDate(this.storage.requestTokenSent)
                && moment().diff(moment(new Date(this.storage.requestTokenSent)), 'seconds') > 30))) {
            this.storage.requestTokenSent = new Date().toISOString();
            let refreshToken = this.storage.refreshToken;
            console.log('Token updating started ' + new Date().toISOString());
            this._updateAccessTokenSubscription = this.http
                .post(`${this.apiUrl}/auth/token/`, {
                    grant_type: 'refresh_token',
                    refresh_token: refreshToken,
                })
                .pipe(
                    map((data) => {
                        console.log('Token updating processing ' + new Date().toISOString());
                        this.processLoginData(
                            this.storage.rememberMe,
                            data,
                            true
                        );
                        let accessToken = this.storage.accessToken;
                        this.logger.log(`Token updated: ${accessToken}`);
                        return true;
                    }),
                    catchError((error) => {
                        console.log('Token updating catch error ' + new Date().toISOString());
                        console.error('Token expired', error);
                        console.log(`refreshToken was: ${refreshToken}`);
                        console.log(`accessToken was: ${this.storage.accessToken}`);
                        console.log(`error.status was: ${error.status}`);
                        if (error instanceof HttpErrorResponse && error.status === 0) {
                            console.log('reloading login');
                            this.logout(true);
                        } else {
                            this.logout();
                        }
                        this.storage.requestTokenSent = null;
                        return of(false);
                    })
                )
                .subscribe((i) => {
                    console.log('Token updating completion' + new Date().toISOString());
                    this._updateAccessTokenSubject.next(i);
                    this._updateAccessTokenSubscription = null;
                    this.storage.requestTokenSent = null;
                });
        }
        return this._updateAccessTokenSubject;
    }

    private processLogoutData(reload?: boolean) {
        this.popupService.destroyAllPopups$.next();
        this.beforeLogoutSubject.next();
        this.storage.clearAll();
        this.updateCurrentTenant(null);
        if (this.currentTimeout) {
            clearTimeout(this.currentTimeout);
        }
        this.afterLogoutSubject.next();
        let router = this.injector.get(RoutingService);
        setTimeout(() => {
            if (reload) {
                router.go(STATE_NAMES.login, {}, true);
                window.location.reload();
            } else {
                router.goToLoginPage();
            }
        }, 10);
        this.logger.debug('Processed logout data');
    }

    private updateCurrentTenant(tenant: User) {
        this._currentTenantSubject.next(tenant);
        this._currentTenantSubscription = null;
    }

    login(signIn: SignIn, log?): Observable<any> {
        return this.http.post(`${this.apiUrl}/auth/token/`, {
                ...signIn.getApiModel(),
                grant_type: 'password',
            })
            .pipe(map((data: any) => {
                if (data.mfa_required) {
                    this.processAccessToken(signIn.rememberMe, data);
                } else {
                    this.storage.companyId = null;
                    this.storage.selectedLevel = null;
                    this.processLoginData(signIn.rememberMe, data, false);
                }
                // Returned so the caller can tell an accepted password that still needs an OTP
                // from a completed sign-in. mfa_required can arrive with a 2xx, and without the
                // payload the subscriber had to assume it was signed in.
                return data;
            })
            // ,
            // tap({
            //     next: val => {
            //         if (val && !val.mfa_required) {
            //             if(log && log.info && log.target) {
            //                 this.activityTrackerService.log(log.info, log.target);
            //             }
            //         }
            //     },
            //     error: error => {
            //         console.log('on error', error.message);
            //     }
            // })
        );
    }

    async logout(reload?: boolean) {
        try {
            await firstValueFrom(this.revokeRefreshToken().pipe(take(1)));
        } catch (e) {
        }
        this.processLogoutData(reload);
        this.logger.log(`Logged out`);
    }

    revokeRefreshToken(): Observable<any> {
        const refreshToken = this.storage.refreshToken;
        if(refreshToken == null) {
            return of(null);
        }
        return this.http.post(`${this.apiUrl}/auth/revoke/`, {
            token: refreshToken}).pipe(
                map((data: any) => data)
            );
    }

    signUp(payload: SignUpApiPayload) {
        return this.http
            .post(`${this.apiUrl}/tenant/`, payload)
            .pipe(
                map((data: any) => {
                    return data;
                })
            );
    }

    checkTenantEmail(email: string, recaptchaResponse?: string) {
        return this.http.post(`${this.apiUrl}/tenant/email/`, {
            email,
            recaptcha_response: recaptchaResponse
        });
    }

    getCurrentTenant(): Observable<User> {
        if (
            !this._currentTenantSubject.value &&
            !this._currentTenantSubscription
        ) {
            let currentUserId = this.storage.userId;
            if (currentUserId > 0) {
                this._currentTenantSubscription = this.getTenant(
                    currentUserId
                ).subscribe({
                    next: (data) => {
                        this.updateCurrentTenant(data);
                    },
                    error: (er) => {
                        this.logger.error(er);
                        this.updateCurrentTenant(null);
                    },
                });
            } else {
                this.logger.error('Current user in null');
                this.updateCurrentTenant(null);
            }
        }
        return this._currentTenantSubject.asObservable();
    }

    activateTenant(uid: string, token: string, newPassword?: string) {
        const body: {uidb64: string; token: string; new_password?: string} = {uidb64: uid, token};
        if (newPassword) {
            body.new_password = newPassword;
        }
        return this.http.put(`${this.apiUrl}/tenant/account/`, body);
    }

    checkActivationLink(uid: string, token: string) {
        return this.http.get(`${this.apiUrl}/tenant/account/`, {
            params: {uidb64: uid, token}
        });
    }

    resendActivation(uid: string) {
        return this.http.post(`${this.apiUrl}/tenant/account/resend/`, {uidb64: uid});
    }

    getTenant(id?: number): Observable<User> {
        let model = this.storage.model;
        let url = model === 'tenant' ? 'tenant' : 'user';
        if (this.storage.userId) {
            return this.http.get(`${this.apiUrl}/${url}/`).pipe(
                map((res) => {
                    this.logger.log(`Fetched user with id: ${id}`);
                    return new User().setFromApiModel(res);
                })
            );
        } else {
            return of();
        }
    }

    getTenantProfile(): Observable<any> {
        if (this.storage.userId) {
            return this.http.get(`${this.apiUrl}/profile/`);
        } else {
            return of();
        }
    }

    setTenantProfile(data: any, name?: string): Observable<any> {
        return this.http.patch(`${this.apiUrl}/profile/`, data)
        .pipe(
            map((res) => {
                if (name) {
                    this.intercom.trackEvent(`set-theme-${name}`);
                }
                if(data['ui_theme_color'] != null) {
                    var theme = ColorThemeFromServerEnum[data['ui_theme_color']];
                    this.intercom.trackEvent(`set-theme_color-${theme}`);
                }
                return res;
            })
        );
    }

    saveTenant(data: any): Observable<User> {
        let model = this.storage.model;
        let url = model === 'tenant' ? 'tenant' : 'user';
        return this.http.patch(`${this.apiUrl}/${url}/`, data).pipe(
            map((res: any) => {
                this.updateCurrentTenant(res);
                return new User().setFromApiModel(res);
            })
        );
    }

    getSubscription(id?: number): Observable<Subscription> {
        return this.http.get(`${this.apiUrl}/subscription/`).pipe(
            map((res: any) => {
                let subscription = new Subscription().setFromApiModel(res);
                return subscription;
            })
        );
    }

    getSubscriptionAndMarkSignup(): Observable<Subscription> {
        return this.http.get(`${this.apiUrl}/subscription/`).pipe(
            map((res: any) => {
                let subscription = new Subscription().setFromApiModel(res);
                if (
                    !subscription.signupTracked &&
                    sessionStorage.customerId !== res.customer_id
                ) {
                    sessionStorage.customerId = res.customer_id;
                    this.markSignupTracked(res.customer_id);
                }
                return subscription;
            })
        );
    }

    markSignupTracked(customer_id: string) {
        this.http
            .patch(`${this.apiUrl}/subscription/`, { ftue: true })
            .subscribe(() => {
                this.firstPromoterService.trackPage(customer_id);
            });
    }

    updateSubscription(): Observable<Subscription> {
        return this.http.put(`${this.apiUrl}/subscription/`, {}).pipe(
            map((res: any) => {
                let subscription = new Subscription().setFromApiModel(res);
                return subscription;
            })
        );
    }

    becomeTenant(): Observable<User> {
        return this.http.put(`${this.apiUrl}/user/convert/`, {}).pipe(
            map((res) => {
                // this.storage.model = res.model;
                this.storage.model = 'tenant';
                return new User().setFromApiModel(res);
            })
        );
    }

    get2faSecret(): Observable<any> {
        return this.http.post(`${this.apiUrl}/mfa/associate/`, {authenticator_type: 'otp'}).pipe(map((data) => data));
    }

    get2faDevice(): Observable<any> {
        return this.http.get(`${this.apiUrl}/mfa/authenticators/`).pipe(map((data: any) => data.results));
    }

    connect2faDevice(mfaToken, otp_token): Observable<any> {
        const body = {
            mfa_token: mfaToken,
            otp: otp_token,
            grant_type: 'http://auth0.com/oauth/grant-type/mfa-otp',
        };
        return this.http.post(`${this.apiUrl}/auth/token/`, body).pipe(map((data: any) => data));
    }

    disconnect2faDevice(id: Number): Observable<any> {
        return this.http.delete(`${this.apiUrl}/mfa/authenticators/${id}/`).pipe(map((data) => data));
    }

    auth2faToken(mfa_token, otp_token): Observable<any> {
        return this.http.post(`${this.apiUrl}/auth/token/`, {
            mfa_token,
            otp: otp_token,
            grant_type: 'http://auth0.com/oauth/grant-type/mfa-otp',
        })
        .pipe(
            map((data: any) => {
                this.storage.companyId = null;
                this.storage.selectedLevel = null;
                this.processLoginData(this.storage.rememberMe , data, false);
                return data;
            })
        );
    }

    getCelloToken(): Observable<any> {
        return this.http.get(`${this.apiUrl}/cello-token/`).pipe(map((data: any) => data));
    }
}
